import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
    getMemberDetailsRequest,
    getProfileVendorRequest,
} from "../../../redux/admin/members/action";
import { VendorBackSvg } from "../../../components/CommonSvg";
import { vendorHasRole } from "../../../utils/authUtils";
import { VendorViewMemberListAccess } from "../../../constant/commonConstants";
import { VENDOR_ACCOUNT_ORDER } from "../../../constant/vendorAccountColumns";
import { invokeApi } from "../../../helper/axiosHelper";

const BASIC_FIELDS = [
    { label: "Name", key: "name" },
    { label: "Account Name", key: "accountName" },
    { label: "Email Address", key: "emailAddress" },
    { label: "Mobile Number", key: "phoneNumberMobileNumber" },
    { label: "Status", key: "status" },
    { label: "Leads Requests", key: "leadsRequests" },
    { label: "Store Name Regular", key: "storeNameRegular" },
    { label: "Field Manager Territory", key: "managerTerritory" },
];

const BUSINESS_FIELDS = [
    { label: "Old HRA", key: "oldHra" },
    { label: "Relationship", key: "relationship" },
    { label: "ACH Status", key: "achEnrolled" },
    { label: "Street Address", key: "streetAddress" },
    { label: "City", key: "city" },
    { label: "County", key: "county" },
    { label: "State", key: "state" },
    { label: "Postal Code", key: "postalCode" },
    { label: "Coke Bottler Region", key: "cokeBottlerRegion" },
    { label: "RBNA Distributor", key: "rbnaDistributor" },
    { label: "BBG Status", key: "bbgStatus" },
    { label: "Business Phone", key: "businessPhone" },
    { label: "Date Enrolled", key: "dateEnrolled" },
    { label: "EIN", key: "ein" },
    { label: "Secondary Email Address", key: "secondaryEmailAddress" },
    { label: "Inactive Date", key: "inactiveDate" },
    { label: "Inactive Reason", key: "inactiveReason" },
    { label: "Mailing Street Address", key: "mailingStreetAddress" },
    { label: "Mailing Address City", key: "mailingAddressCity" },
    { label: "Mailing State", key: "mailingState" },
    { label: "Mailing Zip", key: "mailingZip" },
    { label: "Store Name HRA", key: "storeNameHra" },
    { label: "Ticket Owner", key: "ticketOwner" },
    { label: "User Name", key: "userName" },
    { label: "W9", key: "w9" },
];

const vendorAccountLabel = (apiName) =>
    apiName
        .replace(/_/g, " ")
        .replace(/\s*account numbers?$/i, "")
        .replace(/[\s.]+$/, "")
        .trim();

const fieldValue = (member, key) => {
    if (key === "name") {
        return `${member.firstName || ""} ${member.lastName || ""}`.trim();
    }
    if (key === "w9") {
        if (member.w9 === undefined || member.w9 === null || member.w9 === "") {
            return "";
        }
        return String(member.w9) === "0" ? "No" : "Yes";
    }
    return member[key];
};

const FieldGrid = ({ member, fields }) => (
    <div className="vp-vgrid">
        {fields.map((field) => {
            const value = fieldValue(member, field.key);
            return (
                <div className="vp-vfield" key={field.key}>
                    <span className="vp-vlabel">{field.label}</span>
                    <span className={`vp-vvalue${value ? "" : " is_empty"}`}>
                        {value || "—"}
                    </span>
                </div>
            );
        })}
    </div>
);

const VendorViewMember = ({
    getMemberDetails,
    getVendors,
    memberDetails,
    vendorProfile,
}) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const member = memberDetails || {};
    const [selectedAccountApiNames, setSelectedAccountApiNames] = useState([]);

    useEffect(() => {
        if (!vendorHasRole(VendorViewMemberListAccess)) {
            navigate("/vendor/dashboard");
        }
    }, []);

    useEffect(() => {
        if (id) {
            window.scrollTo({ top: 0, behavior: "smooth" });
            getMemberDetails({ hraMemberId: id });
        }
    }, [id]);

    useEffect(() => {
        getVendors();
    }, []);

    useEffect(() => {
        const loadVendorColumns = async () => {
            try {
                const response = await invokeApi("get", "/vendorColumns", {}, {}, "vendor");
                if (response.status && Array.isArray(response.data?.list)) {
                    setSelectedAccountApiNames(response.data.list);
                } else {
                    setSelectedAccountApiNames([]);
                }
            } catch (error) {
                setSelectedAccountApiNames([]);
            }
        };
        loadVendorColumns();
    }, []);

    const vendorNumbers = member?.vendorNumbers
        ? JSON.parse(member.vendorNumbers)
        : [];

    const accounts = VENDOR_ACCOUNT_ORDER
        .filter((apiName) =>
            vendorProfile?.some((vendor) => vendor.apiName === apiName) &&
            selectedAccountApiNames.includes(apiName)
        )
        .map((apiName) => ({
            apiName: apiName,
            label: vendorAccountLabel(apiName),
            accNumber:
                vendorNumbers.find((item) => item.apiKey === apiName)?.accNumber || "",
        }));

    const subStores = (member?.StoreGroups || [])
        .map((group) => group?.subHra)
        .filter(Boolean);

    return (
        <div className="vp-page">
            <div className="vp-vhead">
                <button
                    type="button"
                    className="vp-vback"
                    title="Back to member stores"
                    onClick={() => navigate("/vendor/members")}
                >
                    <VendorBackSvg />
                </button>
                <div className="vp-vhead-text">
                    <div className="vp-dtitle">
                        {`${member.storeNameRegular || ""}`.trim() ||
                            "Member"}
                    </div>
                </div>
                <span className="vp-hra-chip">{member.hraMemberId || id}</span>
                <span
                    className={`vp-status-pill ${member.status === "Active" ? "st-active" : "st-inactive"}`}
                >
                    {member.status || "—"}
                </span>
            </div>

            <div className="vp-vcard">
                <h6 className="vp-vcard-head">Basic Details</h6>
                <FieldGrid member={member} fields={BASIC_FIELDS} />
            </div>

            <div className="vp-vcard">
                <h6 className="vp-vcard-head">Business Details</h6>
                <FieldGrid member={member} fields={BUSINESS_FIELDS} />
            </div>

            {accounts.length > 0 && (
                <div className="vp-vcard">
                    <h6 className="vp-vcard-head">Vendors Account Number</h6>
                    <div className="vp-vgrid">
                        {accounts.map((account) => (
                            <div className="vp-vfield" key={account.apiName}>
                                <span className="vp-vlabel">{account.label}</span>
                                <span
                                    className={`vp-vvalue${account.accNumber ? "" : " is_empty"}`}
                                >
                                    {account.accNumber || "—"}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {subStores.length > 0 && (
                <div className="vp-vcard">
                    <h6 className="vp-vcard-head">Sub stores ({subStores.length})</h6>
                    <div className="vp-vsubs">
                        {subStores.map((sub) => (
                            <button
                                type="button"
                                className="vp-vsub"
                                key={sub.hraMemberId}
                                onClick={() =>
                                    navigate(`/vendor/members/view/${sub.hraMemberId}`)
                                }
                            >
                                <span className="vp-hra-chip">{sub.hraMemberId}</span>
                                <span className="vp-vsub-store">
                                    {sub.storeNameRegular || "—"}
                                </span>
                                <span className="vp-vsub-name">
                                    {`${sub.firstName || ""} ${sub.lastName || ""}`.trim() ||
                                        "—"}
                                </span>
                                <span
                                    className={`vp-status-pill ${sub.status === "Active" ? "st-active" : "st-inactive"}`}
                                >
                                    {sub.status || "—"}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const mapStateToProps = ({ Members }) => {
    return {
        memberDetails: Members?.memberDetailsApiResponse,
        vendorProfile:
            Members?.proVendor?.list?.length > 0 ? Members.proVendor.list : [],
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        getMemberDetails: (data) => dispatch(getMemberDetailsRequest(data)),
        getVendors: () => dispatch(getProfileVendorRequest()),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(VendorViewMember);
